import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Mail, MapPin, CheckCircle } from 'lucide-react';
import FadeInUp from '../components/FadeInUp';
import SEO from '../components/SEO';

const LinkedInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const SERVICE_AREAS = [
  'Data Architecture',
  'Cloud Platforms',
  'Data Engineering',
  'AI & ML Enablement',
  'Platform Strategy',
  'General Inquiry',
];

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    area: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          botcheck: false,
          ...formData
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (err) {
      toast({
        title: 'Failed to send message',
        description: 'Please try again or email us directly.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pt-32 pb-20">
      <SEO title="Contact" description="Get in touch with Data Kitchen. Book a consultation to discuss your enterprise data platform, cloud migration, or AI enablement needs." path="/contact" />
      <div className="px-8 md:px-16 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <FadeInUp>
            <div className="mb-20">
              <p className="text-xs tracking-[0.3em] uppercase text-silver mb-8">Contact Us</p>
              <p className="text-lg md:text-xl text-soft-white leading-relaxed max-w-3xl">
                Whether you're looking to modernize your data infrastructure or build new AI capabilities,
                we're here to help.
              </p>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.15}>
          <div className="grid md:grid-cols-2 gap-16">
            {/* Left: Form or Success */}
            <div>
              {submitted ? (
                <div className="flex flex-col justify-center py-8">
                  <CheckCircle className="w-14 h-14 text-white mb-8 opacity-80" strokeWidth={1} />
                  <h2 className="text-3xl md:text-4xl font-light mb-4">Inquiry Received</h2>
                  <p className="text-silver text-lg leading-relaxed">
                    Thank you for reaching out. We'll be in touch within 24 hours to discuss your data needs.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-[#141414] border-outline text-white placeholder:text-silver focus:border-silver h-12"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-[#141414] border-outline text-white placeholder:text-silver focus:border-silver h-12"
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      name="company"
                      placeholder="Company Name"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-[#141414] border-outline text-white placeholder:text-silver focus:border-silver h-12"
                    />
                  </div>
                  <div>
                    <Select
                      value={formData.area}
                      onValueChange={(value) => setFormData({ ...formData, area: value })}
                    >
                      <SelectTrigger className="bg-[#141414] border-outline text-white focus:border-silver h-12 w-full">
                        <SelectValue placeholder="Select a service area" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141414] border-outline text-white">
                        {SERVICE_AREAS.map((area) => (
                          <SelectItem
                            key={area}
                            value={area}
                            className="text-white focus:bg-outline focus:text-white"
                          >
                            {area}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Textarea
                      name="message"
                      placeholder="Tell us about your project"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="bg-[#141414] border-outline text-white placeholder:text-silver focus:border-silver resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-white text-black hover:bg-soft-white border-none px-8 py-6 text-sm tracking-wider uppercase transition-all duration-300 w-full md:w-auto"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>

            {/* Right: Contact Information (always visible) */}
            <div className="space-y-12">
              <div>
                <h3 className="text-2xl font-light mb-8">Get in Touch</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <Mail className="w-6 h-6 text-silver flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-silver text-sm mb-1">Email</p>
                      <p className="text-white">hello@datakitchen.com.au</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-silver flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-silver text-sm mb-1">Location</p>
                      <p className="text-white">Sydney, Australia</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <span className="text-silver flex-shrink-0 mt-1"><LinkedInIcon /></span>
                    <div>
                      <p className="text-silver text-sm mb-1">LinkedIn</p>
                      <a
                        href="https://www.linkedin.com/company/datakitchen-au/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white hover:text-soft-white transition-colors duration-300"
                      >
                        Data Kitchen
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-outline pt-12">
                <h4 className="text-xl font-light mb-4">Response Time</h4>
                <p className="text-silver">
                  We typically respond to all inquiries within 24 hours during business days.
                </p>
              </div>
            </div>
          </div>
          </FadeInUp>
        </div>
      </div>
    </div>
  );
};

export default Contact;
